/**
 * Global auth-aware fetch wrapper.
 *
 * Stores the Keycloak Bearer token and injects it on every request.
 * Use this in places where the React useAuth() hook is not available
 * (standalone modules, lib functions, etc.).
 *
 * KeycloakProvider calls setAuthToken() on init and every refresh.
 */

let _token: string | null = null
let _refreshFn: (() => Promise<string | null>) | null = null

export function setAuthToken(t: string | null) {
  _token = t
}

export function getAuthToken(): string | null {
  return _token
}

/** Register a callback that refreshes the token (called by KeycloakProvider). */
export function setTokenRefresher(fn: () => Promise<string | null>) {
  _refreshFn = fn
}

/** Force-refresh the auth token. Returns the new token or null. */
export async function refreshAuthToken(): Promise<string | null> {
  if (!_refreshFn) return _token
  try {
    const newToken = await _refreshFn()
    if (newToken) _token = newToken
    return _token
  } catch {
    return _token
  }
}

/**
 * API call observer, registered by logger.ts (setter injection to avoid the
 * circular import logger -> apiFetch -> logger). Receives every apiFetch
 * outcome, success included. `/api/logs` itself is excluded (feedback loop).
 */
type ApiLogHook = (
  src: string,
  method: string,
  endpoint: string,
  status: number,
  ms: number,
  error?: string,
) => void
let _apiLogHook: ApiLogHook | null = null

export function setApiLogHook(fn: ApiLogHook) {
  _apiLogHook = fn
}

function logApiCall(url: string, method: string, status: number, ms: number, error?: string) {
  if (!_apiLogHook) return
  const path = url.split('?')[0]
  if (path.endsWith('/api/logs')) return
  try {
    _apiLogHook(detectSource(url), method, path, status, Math.round(ms), error)
  } catch { /* logging must never break the call */ }
}

export async function apiFetch(url: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers)
  if (_token) {
    headers.set('Authorization', `Bearer ${_token}`)
  }
  const method = (init?.method || 'GET').toUpperCase()
  const started = performance.now()
  try {
    const res = await fetch(url, { ...init, headers })
    logApiCall(url, method, res.status, performance.now() - started)
    return res
  } catch (err) {
    logApiCall(url, method, 0, performance.now() - started, (err as Error).message)
    throw err
  }
}

/**
 * Detect source module from URL path for logging.
 */
function detectSource(url: string): string {
  if (url.includes('/drive')) return 'drive';
  if (url.includes('/mail')) return 'mail';
  if (url.includes('/messaging')) return 'messaging';
  if (url.includes('/chat')) return 'chat';
  if (url.includes('/search')) return 'search';
  if (url.includes('/calendar')) return 'calendar';
  if (url.includes('/notes')) return 'notes';
  if (url.includes('/contacts')) return 'contacts';
  if (url.includes('/shadow')) return 'shadow';
  if (url.includes('/android')) return 'android';
  return 'system';
}

/**
 * Verbose response check — throws with full context on HTTP error.
 * Also pushes to ActivityLog (Live tab) and Logger (persistent).
 * Use: `await verboseCheck(res, "upload file")` instead of `if (!res.ok) throw ...`
 */
export async function verboseCheck(
  r: Response,
  context = "API request",
): Promise<Response> {
  if (!r.ok) {
    let detail = "";
    try { const j = await r.json(); detail = (j as Record<string,unknown>).detail as string || JSON.stringify(j); } catch { try { detail = await r.text(); } catch { /* ignore */ } }
    const url = r.url || "";
    const safeUrl = url.replace(/([?&])token=[^&]*/g, "$1token=***");
    const msg = `${context} — HTTP ${r.status} ${r.statusText}${detail ? ` — ${detail}` : ""}${safeUrl ? ` — ${safeUrl}` : ""}`;
    const source = detectSource(url);

    // Push to Logger (Live tab + Redis → S3)
    try {
      const { log } = await import('./logger');
      log.error(source, msg, { status: r.status, url, detail });
    } catch { /* ignore if Logger not available */ }

    throw new Error(msg);
  }
  return r;
}

/** Append auth token to a URL for direct browser requests (<img src>, <video src>, etc.) */
export function authUrl(url: string): string {
  if (!_token) return url
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}token=${encodeURIComponent(_token)}`
}
